import { NameValueModel } from "@services/common/model/gloabal.model";
import { STANDARD_RESIDUES } from "@services/common/config/common.config";
import { gzip } from "pako";
import {
  SequenceModel,
  ChainModel,
  EnhancedProteinStructureModel,
  AntibodyFileMode,
  AntibodySequenceModel,
} from "@services/task/model/task.model";
import { SourceEnum } from "@services/task/enum/task.enum";
import { convertPdbToCifApi } from "@services/home/home.api";

interface ChainMapModel {
  [key: string]: {
    heavy?: NameValueModel;
    light?: NameValueModel;
  };
}

export function countAlphabet(sequence: string): number {
  if (!sequence) return 0;
  const rx = new RegExp(`[${STANDARD_RESIDUES}]`, "g");
  const m = sequence.match(rx);

  if (m) {
    return m.length;
  } else {
    return 0;
  }
}
export function getFileName(url: string, noExtension = false) {
  if (url) {
    const filename = url.substring(url.lastIndexOf("/") + 1);

    if (noExtension) {
      return filename.split(".")[0];
    } else {
      return filename;
    }
  }
  return "";
}

export function replaceNewline(text: string) {
  if (!text) return text;

  const htmlText = text.replace(/\r?(\n|\\+n)/g, "</br>");

  return htmlText;
}

export function download(data: string, filename = "data") {
  const link = document.createElement("a");
  link.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(data)
  );
  link.setAttribute("download", filename);
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadZIP(data: any, filename = "data") {
  const blob = new Blob([data], { type: "application/zip" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function countDeNovo(sequence: string) {
  if (!sequence) return 0;
  const reg = /\[[HL]\d+,\d+\]/g;
  const arr = sequence.match(reg);
  let c = 0;
  if (arr) {
    c = arr.reduce((ac, m) => {
      const [, num] = m.split(",");
      return ac + parseInt(num);
    }, c);
  }
  const remain = sequence.replace(/\[[A-Z,\d]*\]/g, "");
  return c + remain.length;
}

export function processAntibodyAntigenSequence(
  content: string,
  fileName: string
): EnhancedProteinStructureModel | null {
  const str = content.replace(/\r+\n/g, "\n");
  const res = str.split(">").filter((item) => !!item);
  const list: NameValueModel[] = res.map((s) => {
    const [name, ...rest] = s.split("\n");
    return {
      name,
      value: rest.join("\n"),
    };
  });

  const reg = /^_[A-Za-z0-9]$/;

  const checkNameValid = list.every((item) => {
    const name = item.name;
    const suffix = name.slice(-2);
    return suffix.match(reg);
  });

  if (!checkNameValid) {
    return null;
  }

  const map: Record<string, ChainModel[]> = {};

  list.forEach((item) => {
    const name = item.name.slice(0, -2);
    const chainName = item.name.slice(-1);
    if (!map[name]) {
      map[name] = [
        {
          chainName,
          chainValue: item.value,
        },
      ];
    } else {
      map[name].push({
        chainName,
        chainValue: item.value,
      });
    }
  });

  const result: SequenceModel[] = Object.keys(map).map((name) => {
    return {
      name,
      value: map[name],
    };
  });

  return {
    filename: fileName.split(".")[0],
    enhancedSequences: result,
  };
}

export function parseProteinStructurePredictionEnhanced(file: File) {
  return new Promise<EnhancedProteinStructureModel>((resolve, reject) => {
    const r = new FileReader();
    r.onload = function () {
      const res = processAntibodyAntigenSequence(r.result as string, file.name);
      if (res) {
        resolve(res);
      } else {
        reject("Invalid Protein name");
      }
    };
    r.addEventListener("error", (e) => {
      reject(e);
    });
    r.readAsText(file);
  });
}

export function processDeNovoAntibodyDesignFasta(
  content: string,
  fileName: string
):
  | { filename: string; sequences: AntibodySequenceModel[] }
  | { errorMsg: string } {
  const res = content.split(">").filter((item) => !!item);
  const list: NameValueModel[] = res.map((s) => {
    const [name, ...rest] = s.split("\n");
    return {
      name,
      value: rest.join("\n"),
    };
  });

  const chainNameValid = list.every((item) => {
    return item.name.endsWith("_H") || item.name.endsWith("_L");
  });

  if (!chainNameValid) {
    return {
      errorMsg: "invalid chain name for antibody",
    };
  }

  const chainMap: ChainMapModel = {};

  list.forEach((item) => {
    const name = item.name.slice(0, -2);
    const suffix = item.name.slice(-2);
    if (chainMap[name]) {
      if (suffix === "_H") {
        chainMap[name].heavy = item;
      } else {
        chainMap[name].light = item;
      }
    } else {
      if (suffix === "_H") {
        chainMap[name] = {
          heavy: item,
        };
      } else {
        chainMap[name] = {
          light: item,
        };
      }
    }
  });

  if (Object.keys(chainMap).length !== 1) {
    return {
      errorMsg: "only one pair allowed",
    };
  }

  const chainPairValid = Object.keys(chainMap).every((name) => {
    return !!chainMap[name].heavy && !!chainMap[name].light;
  });

  if (!chainPairValid) {
    return {
      errorMsg: "unpaired chains for antibody",
    };
  }

  const sequences: AntibodySequenceModel[] = Object.keys(chainMap).map(
    (name) => {
      return {
        name,
        heavy: chainMap[name].heavy?.value,
        light: chainMap[name].light?.value,
      } as AntibodySequenceModel;
    }
  );

  return {
    filename: fileName.split(".")[0],
    sequences,
  };
}

export function parseDeNovoAntibodyDesignFastaFile(file: File) {
  return new Promise<AntibodyFileMode>((resolve, reject) => {
    const r = new FileReader();
    r.onload = function () {
      const str = (r.result as string).replace(/\r+\n/g, "\n");
      const result = processDeNovoAntibodyDesignFasta(str, file.name);

      if ("errorMsg" in result) {
        reject(result.errorMsg);
      } else {
        resolve(result);
      }
    };

    r.addEventListener("error", (e) => {
      reject(e);
    });
    r.readAsText(file);
  });
}

export function readFileContent(file: File) {
  return new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = function () {
      const str = (r.result as string).replace(/\r+\n/g, "\n");
      resolve(str);
    };

    r.addEventListener("error", (e) => {
      reject(e);
    });
    r.readAsText(file);
  });
}

export function getGzipFormData(file: File, source: SourceEnum) {
  return new Promise<FormData>((resolve) => {
    const reader = new FileReader();
    reader.onload = function () {
      const convertedData = new Uint8Array(reader.result as ArrayBuffer);
      const zipped = gzip(convertedData);
      const convertedZip = new Uint8Array(zipped);
      const blob = new Blob([convertedZip], { type: file.type });
      const formData = new FormData();
      formData.append("file", blob, file.name);
      formData.append("source", source as string);

      resolve(formData);
    };

    reader.readAsArrayBuffer(file);
  });
}

export function readPdbFileContent(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);

    reader.readAsText(file);
  });
}

export async function convertFile(file: File) {
  const content = await readPdbFileContent(file);
  const result = await convertPdbToCifApi({
    pdb_content: content,
  });

  const nameArr = file.name.split(".");
  nameArr.pop();

  const newFile = new File(
    [result.data.cif_content],
    `${nameArr.join("")}.cif`,
    {
      type: "text/plain", // MIME type
      lastModified: Date.now(),
    }
  );

  return newFile;
}
